import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface UserAccount {
  attributes: {
    ['api-msisdn']: Array<string>;
    ['api-tokens']: Array<string>;
    ['provider-code']: Array<string>;
  };
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  username: string;
  roles: Array<string>;
  canManageRoles: boolean;
}

export interface AuthenticationState {
  isAuth: boolean;
  init: boolean;
}

export interface DecodedTokenPartial {
  resource_access: {
    'provider-panel-backend-client': {
      roles: Array<string>;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _authenticated$: BehaviorSubject<AuthenticationState> = new BehaviorSubject<AuthenticationState>({
    isAuth: false,
    init: false,
  });

  private readonly _user$ = new ReplaySubject<UserAccount>(1);

  constructor(private readonly _keycloakService: KeycloakService, private readonly _http: HttpClient) {
    this._keycloakService.getToken().then(token => {
      const decodedToken: DecodedTokenPartial | null = new JwtHelperService().decodeToken(token);

      this._authenticated$.next({
        isAuth: !!token,
        init: true,
      });
      
      const roles = decodedToken?.resource_access['provider-panel-backend-client'].roles || [];

      this.getUserAccount$().subscribe(user => {
        this._user$.next({
          ...user,
          roles,
          canManageRoles: roles.includes(environment.canManageRoles),
        });
      });
    });
  }

  public get authenticated$(): Observable<AuthenticationState> {
    return this._authenticated$.asObservable();
  }

  public get user$(): Observable<UserAccount> {
    return this._user$.asObservable();
  }

  public logout(): void {
    this._keycloakService.logout();
  }

  private getUserAccount$(): Observable<UserAccount> {
    const { url, realm } = environment.keycloakConfig;
    return this._http.get<UserAccount>(`${url}/realms/${realm}/account`);
  }
}
